/**
 * ValidationRuleEditor Component
 * 
 * Editor for individual validation rules with support for different rule types.
 */

import React from 'react';
import { Input, InputNumber, Select, Switch, Space, Radio, Button } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { 
  ValidationRule, 
  ValidationSeverity, 
  ValidationOperator,
  NumberValidationRule,
  BooleanValidationRule,
  RangeValidationRule,
  ComparisonValidationRule,
  CalculationValidationRule,
  TimeRangeValidationRule,
  ArrayValidationRule,
  PropertyValidationRule,
  ComplexValidationRule,
  ImprovementArtifactValidationRule,
  ItemGenerationValidationRule,
} from '../../types/transitionParameters';
import { PropertySelector } from '../PrimeModel/PropertySelector';
import './ValidationRuleEditor.css';

const { Option } = Select;
const { TextArea } = Input;

interface PropertyDefinition {
  property_key?: string;
  name?: string;
  property_type?: string;
  type?: string;
  description?: string;
}

interface TransitionOption {
  name: string;
  id?: string;
  description?: string;
}

interface ValidationRuleEditorProps {
  label: string;
  rule: ValidationRule | undefined;
  onChange: (rule: ValidationRule) => void;
  disabled?: boolean;
  artifactProperties?: PropertyDefinition[]; // Properties available for property-based rules
  transitions?: TransitionOption[]; // Available transitions for selection (e.g., for improvement artifact check)
}

export const ValidationRuleEditor: React.FC<ValidationRuleEditorProps> = ({
  label,
  rule,
  onChange,
  disabled = false,
  artifactProperties = [],
  transitions = [],
}) => {
  const ruleType = rule?.type || 'number';

  const handleChange = (field: string, value: any) => {
    onChange({
      ...rule,
      [field]: value,
    } as ValidationRule);
  };

  const renderRuleSpecificFields = () => {
    switch (ruleType) {
      case 'number': {
        const numRule = rule as NumberValidationRule;
        return (
          <>
            <div className="validation-field">
              <label>Operator:</label>
              <Select
                value={numRule?.operator || '>='}
                onChange={(value) => handleChange('operator', value)}
                disabled={disabled}
                style={{ width: '100%' }}
              >
                <Option value=">">Greater than (&gt;)</Option>
                <Option value=">=">Greater than or equal (&gt;=)</Option>
                <Option value="<">Less than (&lt;)</Option>
                <Option value="<=">Less than or equal (&lt;=)</Option>
                <Option value="==">Equal (==)</Option>
                <Option value="!=">Not equal (!=)</Option>
              </Select>
            </div>
            <div className="validation-field">
              <label>Value:</label>
              <InputNumber
                value={numRule?.value ?? null}
                onChange={(value) => handleChange('value', value)}
                disabled={disabled}
                placeholder="Comparison value"
                style={{ width: '100%' }}
              />
            </div>
          </>
        );
      }

      case 'boolean': {
        const boolRule = rule as BooleanValidationRule;
        return (
          <>
            <div className="validation-field">
              <label>Required Value:</label>
              <Switch
                checked={boolRule?.value ?? false}
                onChange={(checked) => handleChange('value', checked)}
                disabled={disabled}
              />
            </div>
            <div className="validation-field">
              <label>Exceptions (comma-separated):</label>
              <Select
                mode="tags"
                value={boolRule?.exceptions || []}
                onChange={(value) => handleChange('exceptions', value)}
                disabled={disabled}
                placeholder="e.g., CREATION"
                style={{ width: '100%' }}
              />
            </div>
            <div className="validation-field">
              <label>Allowed For (comma-separated):</label>
              <Select
                mode="tags"
                value={boolRule?.allowed_for || []}
                onChange={(value) => handleChange('allowed_for', value)}
                disabled={disabled}
                placeholder="e.g., MATERIAL, RESOURCE"
                style={{ width: '100%' }}
              />
            </div>
          </>
        );
      }

      case 'range': {
        const rangeRule = rule as RangeValidationRule;
        return (
          <>
            <div className="validation-field">
              <label>Minimum:</label>
              <InputNumber
                value={rangeRule?.min ?? null}
                onChange={(value) => handleChange('min', value)}
                disabled={disabled}
                placeholder="Min value"
                style={{ width: '100%' }}
              />
            </div>
            <div className="validation-field">
              <label>Maximum:</label>
              <InputNumber
                value={rangeRule?.max ?? null}
                onChange={(value) => handleChange('max', value)}
                disabled={disabled}
                placeholder="Max value"
                style={{ width: '100%' }}
              />
            </div>
          </>
        );
      }

      case 'comparison': {
        const compRule = rule as ComparisonValidationRule;
        return (
          <>
            <div className="validation-field">
              <label>Field 1:</label>
              <Input
                value={compRule?.field1 || ''}
                onChange={(e) => handleChange('field1', e.target.value)}
                disabled={disabled}
                placeholder="First field name"
              />
            </div>
            <div className="validation-field">
              <label>Operator:</label>
              <Select
                value={compRule?.operator || '<='}
                onChange={(value) => handleChange('operator', value)}
                disabled={disabled}
                style={{ width: '100%' }}
              >
                <Option value=">">Greater than</Option>
                <Option value=">=">Greater than or equal</Option>
                <Option value="<">Less than</Option>
                <Option value="<=">Less than or equal</Option>
                <Option value="==">Equal</Option>
                <Option value="!=">Not equal</Option>
              </Select>
            </div>
            <div className="validation-field">
              <label>Field 2:</label>
              <Input
                value={compRule?.field2 || ''}
                onChange={(e) => handleChange('field2', e.target.value)}
                disabled={disabled}
                placeholder="Second field name"
              />
            </div>
          </>
        );
      }

      case 'calculation': {
        const calcRule = rule as CalculationValidationRule;
        return (
          <>
            <div className="validation-field full-width">
              <label>Formula:</label>
              <Input
                value={calcRule?.formula || ''}
                onChange={(e) => handleChange('formula', e.target.value)}
                disabled={disabled}
                placeholder="e.g., scrap_rate + defect_rate"
              />
            </div>
            <div className="validation-field">
              <label>Operator:</label>
              <Select
                value={calcRule?.operator || '<='}
                onChange={(value) => handleChange('operator', value)}
                disabled={disabled}
                style={{ width: '100%' }}
              >
                <Option value=">">Greater than</Option>
                <Option value=">=">Greater than or equal</Option>
                <Option value="<">Less than</Option>
                <Option value="<=">Less than or equal</Option>
                <Option value="==">Equal</Option>
                <Option value="!=">Not equal</Option>
              </Select>
            </div>
            <div className="validation-field">
              <label>Value:</label>
              <InputNumber
                value={calcRule?.value ?? null}
                onChange={(value) => handleChange('value', value)}
                disabled={disabled}
                placeholder="Comparison value"
                style={{ width: '100%' }}
              />
            </div>
          </>
        );
      }

      case 'time_range': {
        const timeRule = rule as TimeRangeValidationRule;
        return (
          <>
            <div className="validation-field">
              <label>Start Time:</label>
              <Input
                value={timeRule?.start || ''}
                onChange={(e) => handleChange('start', e.target.value)}
                disabled={disabled}
                placeholder="e.g., 08:00"
              />
            </div>
            <div className="validation-field">
              <label>End Time:</label>
              <Input
                value={timeRule?.end || ''}
                onChange={(e) => handleChange('end', e.target.value)}
                disabled={disabled}
                placeholder="e.g., 17:00"
              />
            </div>
          </>
        );
      }

      case 'array': {
        const arrayRule = rule as ArrayValidationRule;
        return (
          <div className="validation-field full-width">
            <label>Allowed Values:</label>
            <Select
              mode="tags"
              value={arrayRule?.values || []}
              onChange={(value) => handleChange('values', value)}
              disabled={disabled}
              placeholder="Enter allowed values"
              style={{ width: '100%' }}
            />
          </div>
        );
      }

      case 'property': {
        const propRule = rule as PropertyValidationRule;
        const selectedProperty = artifactProperties.find(
          p => (p.property_key || p.name) === propRule?.property_key
        );
        const propertyType = selectedProperty?.property_type || selectedProperty?.type || 'string';
        const isDateProperty = propertyType === 'date' || propertyType === 'datetime';
        
        return (
          <>
            <div className="validation-field full-width">
              <label>Property:</label>
              <PropertySelector
                properties={artifactProperties}
                value={propRule?.property_key}
                onChange={(propertyKey) => {
                  handleChange('property_key', propertyKey);
                  // Reset date comparison if property type changes
                  const newProp = artifactProperties.find(
                    p => (p.property_key || p.name) === propertyKey
                  );
                  const newPropType = newProp?.property_type || newProp?.type || 'string';
                  if (newPropType !== 'date' && newPropType !== 'datetime') {
                    handleChange('use_date_comparison', false);
                  }
                }}
                disabled={disabled}
                placeholder="Select property to validate"
              />
            </div>
            
            {isDateProperty && (
              <div className="validation-field">
                <label>Date Comparison Type:</label>
                <Radio.Group
                  value={propRule?.use_date_comparison ? 'date' : 'value'}
                  onChange={(e) => {
                    handleChange('use_date_comparison', e.target.value === 'date');
                    if (e.target.value === 'value') {
                      handleChange('date_comparison_type', undefined);
                      handleChange('date_start_property', undefined);
                      handleChange('date_end_property', undefined);
                    }
                  }}
                  disabled={disabled}
                >
                  <Radio value="value">Compare to Value</Radio>
                  <Radio value="date">Date Range Check</Radio>
                </Radio.Group>
              </div>
            )}
            
            {propRule?.use_date_comparison && isDateProperty ? (
              <>
                <div className="validation-field">
                  <label>Date Comparison:</label>
                  <Select
                    value={propRule?.date_comparison_type || 'today_between'}
                    onChange={(value) => handleChange('date_comparison_type', value)}
                    disabled={disabled}
                    style={{ width: '100%' }}
                  >
                    <Option value="today_between">Today is between dates</Option>
                    <Option value="date_between">Property date is between dates</Option>
                    <Option value="date_after">Date is after start date</Option>
                    <Option value="date_before">Date is before end date</Option>
                  </Select>
                </div>
                {(propRule?.date_comparison_type === 'today_between' || 
                  propRule?.date_comparison_type === 'date_between') && (
                  <>
                    <div className="validation-field full-width">
                      <label>Start Date Property:</label>
                      <PropertySelector
                        properties={artifactProperties.filter(p => {
                          const key = p.property_key || p.name || '';
                          const propType = p.property_type || p.type || '';
                          return key !== propRule?.property_key && 
                                 (propType === 'date' || propType === 'datetime');
                        })}
                        value={propRule?.date_start_property}
                        onChange={(propertyKey) => handleChange('date_start_property', propertyKey)}
                        disabled={disabled}
                        placeholder="Select start date property"
                      />
                    </div>
                    <div className="validation-field full-width">
                      <label>End Date Property:</label>
                      <PropertySelector
                        properties={artifactProperties.filter(p => {
                          const key = p.property_key || p.name || '';
                          const propType = p.property_type || p.type || '';
                          return key !== propRule?.property_key && 
                                 (propType === 'date' || propType === 'datetime');
                        })}
                        value={propRule?.date_end_property}
                        onChange={(propertyKey) => handleChange('date_end_property', propertyKey)}
                        disabled={disabled}
                        placeholder="Select end date property"
                      />
                    </div>
                  </>
                )}
                {(propRule?.date_comparison_type === 'date_after' || 
                  propRule?.date_comparison_type === 'date_before') && (
                  <div className="validation-field full-width">
                    <label>Reference Date Property:</label>
                    <PropertySelector
                      properties={artifactProperties.filter(p => {
                        const key = p.property_key || p.name || '';
                        const propType = p.property_type || p.type || '';
                        return key !== propRule?.property_key && 
                               (propType === 'date' || propType === 'datetime');
                      })}
                      value={propRule?.date_start_property}
                      onChange={(propertyKey) => handleChange('date_start_property', propertyKey)}
                      disabled={disabled}
                      placeholder="Select reference date property"
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="validation-field">
                  <label>Validation Type:</label>
                  <Radio.Group
                    value={propRule?.validate_format ? 'format' : 'compare'}
                    onChange={(e) => {
                      handleChange('validate_format', e.target.value === 'format');
                      if (e.target.value === 'compare') {
                        handleChange('format_type', undefined);
                        handleChange('format_pattern', undefined);
                      }
                    }}
                    disabled={disabled}
                  >
                    <Radio value="compare">Compare Value</Radio>
                    <Radio value="format">Validate Format</Radio>
                  </Radio.Group>
                </div>
                
                {propRule?.validate_format ? (
                  <>
                    <div className="validation-field">
                      <label>Format Type:</label>
                      <Select
                        value={propRule?.format_type || 'email'}
                        onChange={(value) => {
                          handleChange('format_type', value);
                          if (value !== 'regex') {
                            handleChange('format_pattern', undefined);
                          }
                        }}
                        disabled={disabled}
                        style={{ width: '100%' }}
                      >
                        <Option value="email">Email Address</Option>
                        <Option value="url">URL</Option>
                        <Option value="phone">Phone Number</Option>
                        <Option value="date">Date (YYYY-MM-DD)</Option>
                        <Option value="datetime">Date/Time (ISO 8601)</Option>
                        <Option value="number">Number (decimal)</Option>
                        <Option value="integer">Integer</Option>
                        <Option value="regex">Custom Regex Pattern</Option>
                      </Select>
                    </div>
                    {propRule?.format_type === 'regex' && (
                      <div className="validation-field full-width">
                        <label>Regex Pattern:</label>
                        <Input
                          value={propRule?.format_pattern || ''}
                          onChange={(e) => handleChange('format_pattern', e.target.value)}
                          disabled={disabled}
                          placeholder="e.g., ^[A-Z0-9]+$"
                          style={{ width: '100%' }}
                        />
                        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                          Enter a valid regular expression pattern
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="validation-field">
                      <label>Operator:</label>
                      <Select
                        value={propRule?.operator || '=='}
                        onChange={(value) => handleChange('operator', value)}
                        disabled={disabled}
                        style={{ width: '100%' }}
                      >
                        <Option value=">">Greater than (&gt;)</Option>
                        <Option value=">=">Greater than or equal (&gt;=)</Option>
                        <Option value="<">Less than (&lt;)</Option>
                        <Option value="<=">Less than or equal (&lt;=)</Option>
                        <Option value="==">Equal (==)</Option>
                        <Option value="!=">Not equal (!=)</Option>
                      </Select>
                    </div>
                    <div className="validation-field">
                      <label>Compare To:</label>
                      <Radio.Group
                        value={propRule?.use_property_value ? 'property' : 'literal'}
                        onChange={(e) => {
                          handleChange('use_property_value', e.target.value === 'property');
                          if (e.target.value === 'literal') {
                            handleChange('compare_to_property', undefined);
                          }
                        }}
                        disabled={disabled}
                      >
                        <Radio value="literal">Literal Value</Radio>
                        <Radio value="property">Another Property</Radio>
                      </Radio.Group>
                    </div>
                  </>
                )}
                {propRule?.use_property_value ? (
                  <div className="validation-field full-width">
                    <label>Compare To Property:</label>
                    <PropertySelector
                      properties={artifactProperties.filter(p => {
                        const key = p.property_key || p.name || '';
                        return key !== propRule?.property_key;
                      })}
                      value={propRule?.compare_to_property}
                      onChange={(propertyKey) => handleChange('compare_to_property', propertyKey)}
                      disabled={disabled}
                      placeholder="Select property to compare against"
                    />
                  </div>
                ) : (
                  <div className="validation-field">
                    <label>Value:</label>
                    {propertyType === 'boolean' ? (
                      <Select
                        value={propRule?.value}
                        onChange={(value) => handleChange('value', value)}
                        disabled={disabled}
                        style={{ width: '100%' }}
                      >
                        <Option value={true}>True</Option>
                        <Option value={false}>False</Option>
                      </Select>
                    ) : (propertyType === 'number' || propertyType === 'integer' || propertyType === 'float') ? (
                      <InputNumber
                        value={propRule?.value}
                        onChange={(value) => handleChange('value', value)}
                        disabled={disabled}
                        style={{ width: '100%' }}
                        placeholder="Comparison value"
                      />
                    ) : (
                      <Input
                        value={propRule?.value ?? ''}
                        onChange={(e) => handleChange('value', e.target.value)}
                        disabled={disabled}
                        placeholder="Comparison value"
                        style={{ width: '100%' }}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </>
        );
      }
      
      case 'complex': {
        const complexRule = rule as ComplexValidationRule;
        return (
          <>
            <div className="validation-field">
              <label>Logic:</label>
              <Select
                value={complexRule?.logic || 'AND'}
                onChange={(value) => handleChange('logic', value)}
                disabled={disabled}
                style={{ width: '100%' }}
              >
                <Option value="AND">AND (all conditions must pass)</Option>
                <Option value="OR">OR (any condition must pass)</Option>
              </Select>
            </div>
            <div className="validation-field full-width">
              <label>Conditions:</label>
              <div style={{ marginTop: 8 }}>
                {complexRule?.conditions?.map((condition, index) => (
                  <div key={index} style={{ marginBottom: 8, padding: 8, border: '1px solid #d9d9d9', borderRadius: 4 }}>
                    <ValidationRuleEditor
                      label={`Condition ${index + 1}`}
                      rule={condition}
                      onChange={(updatedCondition) => {
                        const updatedConditions = [...(complexRule?.conditions || [])];
                        updatedConditions[index] = updatedCondition;
                        handleChange('conditions', updatedConditions);
                      }}
                      disabled={disabled}
                      artifactProperties={artifactProperties}
                    />
                    {!disabled && (
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          const updatedConditions = complexRule?.conditions?.filter((_, i) => i !== index) || [];
                          handleChange('conditions', updatedConditions);
                        }}
                        style={{ marginTop: 8 }}
                      >
                        Remove Condition
                      </Button>
                    )}
                  </div>
                )) || <div style={{ color: '#999' }}>No conditions defined</div>}
                {!disabled && (
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      const newCondition: ValidationRule = {
                        type: 'property',
                        property_key: '',
                        operator: '==',
                        value: null,
                        severity: 'error',
                      };
                      const updatedConditions = [...(complexRule?.conditions || []), newCondition];
                      handleChange('conditions', updatedConditions);
                    }}
                    style={{ marginTop: 8 }}
                  >
                    Add Condition
                  </Button>
                )}
              </div>
            </div>
          </>
        );
      }

      case 'improvement_artifact_check': {
        const improvementRule = rule as ImprovementArtifactValidationRule;
        return (
          <>
            <div className="validation-field full-width">
              <label>Check Type:</label>
              <Select
                value={improvementRule?.check_type || 'most_recent'}
                onChange={(value) => handleChange('check_type', value)}
                disabled={disabled}
                style={{ width: '100%' }}
              >
                <Option value="most_recent">Most Recent</Option>
                <Option value="any">Any</Option>
                <Option value="active_only">Active Only</Option>
              </Select>
              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                How to select the improvement artifact_item to check
              </div>
            </div>
            <div className="validation-field">
              <label>Require Active:</label>
              <Switch
                checked={improvementRule?.require_active ?? true}
                onChange={(checked) => handleChange('require_active', checked)}
                disabled={disabled}
              />
              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                Require improvement to be validated and within effective dates
              </div>
            </div>
            <div className="validation-field">
              <label>Auto Create If Missing:</label>
              <Switch
                checked={improvementRule?.auto_create_if_missing ?? true}
                onChange={(checked) => handleChange('auto_create_if_missing', checked)}
                disabled={disabled}
              />
              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                Automatically trigger creation transition if improvement doesn't exist
              </div>
            </div>
            {improvementRule?.auto_create_if_missing && (
              <div className="validation-field full-width">
                <label>Creation Transition Name:</label>
                {transitions.length > 0 ? (
                  <Select
                    value={improvementRule?.creation_transition_name || undefined}
                    onChange={(value) => handleChange('creation_transition_name', value)}
                    disabled={disabled}
                    placeholder="Select a transition"
                    showSearch
                    optionFilterProp="label"
                    style={{ width: '100%' }}
                  >
                    {transitions.map((transition) => (
                      <Option key={transition.name} value={transition.name} label={transition.name}>
                        {transition.name}
                        {transition.description && (
                          <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>
                            - {transition.description}
                          </span>
                        )}
                      </Option>
                    ))}
                  </Select>
                ) : (
                  <Input
                    value={improvementRule?.creation_transition_name || ''}
                    onChange={(e) => handleChange('creation_transition_name', e.target.value)}
                    disabled={disabled}
                    placeholder="e.g., Create Improvement"
                  />
                )}
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  {transitions.length > 0
                    ? 'Select the transition to trigger for creating the improvement artifact_item'
                    : 'Name of the transition to trigger for creating the improvement artifact_item'}
                </div>
              </div>
            )}
            <div className="validation-field full-width">
              <label>Error Message (optional):</label>
              <TextArea
                value={improvementRule?.error_message || ''}
                onChange={(e) => handleChange('error_message', e.target.value)}
                disabled={disabled}
                placeholder="e.g., Improvement artifact_item must be active before proceeding"
                rows={2}
              />
              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                Custom error message to display if validation fails
              </div>
            </div>
          </>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="validation-rule-editor">
      <div className="validation-header">
        <label className="validation-label">{label}</label>
      </div>

      <div className="validation-fields">
        {/* Rule Type */}
        <div className="validation-field">
          <label>Type:</label>
          <Select
            value={ruleType}
            onChange={(value) => {
              // Create new rule with correct type
              const newRule: any = {
                type: value,
                severity: rule?.severity || 'error',
                description: rule?.description,
              };
              
              // Add type-specific defaults
              if (value === 'number') {
                newRule.operator = '>=';
                newRule.value = 0;
              } else if (value === 'range') {
                newRule.min = 0;
                newRule.max = 1;
              } else if (value === 'property') {
                newRule.operator = '==';
                newRule.use_property_value = false;
                newRule.use_date_comparison = false;
              } else if (value === 'complex') {
                newRule.logic = 'AND';
                newRule.conditions = [];
        } else if (value === 'improvement_artifact_check') {
          newRule.check_type = 'most_recent';
          newRule.require_active = true;
          newRule.auto_create_if_missing = true;
        } else if (value === 'item_generation_check') {
          newRule.generation_type = 'singleton';
        }
              
              onChange(newRule as ValidationRule);
            }}
            disabled={disabled}
            style={{ width: '100%' }}
          >
            <Option value="number">Number Comparison</Option>
            <Option value="boolean">Boolean Check</Option>
            <Option value="range">Range Check</Option>
            <Option value="comparison">Field Comparison</Option>
            <Option value="calculation">Formula Calculation</Option>
            <Option value="time_range">Time Range</Option>
            <Option value="array">Array Values</Option>
            {artifactProperties.length > 0 && (
              <>
                <Option value="property">Property-Based</Option>
                <Option value="complex">Complex (AND/OR Logic)</Option>
              </>
            )}
              <Option value="improvement_artifact_check">Improvement Artifact Check</Option>
              <Option value="item_generation_check">Item Generation Check</Option>
            </Select>
        </div>

        {/* Severity */}
        <div className="validation-field">
          <label>Severity:</label>
          <Select
            value={rule?.severity || 'error'}
            onChange={(value) => handleChange('severity', value)}
            disabled={disabled}
            style={{ width: '100%' }}
          >
            <Option value="error">
              <span style={{ color: '#ff4d4f' }}>● Error</span>
            </Option>
            <Option value="warning">
              <span style={{ color: '#faad14' }}>● Warning</span>
            </Option>
            <Option value="info">
              <span style={{ color: '#1890ff' }}>● Info</span>
            </Option>
          </Select>
        </div>

        {/* Type-specific fields */}
        {renderRuleSpecificFields()}

        {/* Description */}
        <div className="validation-field full-width">
          <label>Description:</label>
          <TextArea
            value={rule?.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            disabled={disabled}
            rows={2}
            placeholder="Describe this validation rule"
          />
        </div>
      </div>
    </div>
  );
};

